import { AUDIT_ACTIONS, type AuditEntry, type StaffListItem } from "@vaulted/shared";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get } from "../api";
import { Alert, Button, Field, Loading, PageHeader, Panel, SelectField, formatDateTime } from "../components/ui";
import { useSession } from "../session";

type Page = { entries: AuditEntry[]; nextCursor: string | null };

function describe(e: AuditEntry): string {
  const base = AUDIT_ACTIONS[e.action as keyof typeof AUDIT_ACTIONS] ?? e.action;
  return e.targetLabel && !e.action.startsWith("auth.") ? `${base}: ${e.targetLabel}` : base;
}

function detailText(e: AuditEntry): string | null {
  const d = e.details;
  if (!d || !Object.keys(d).length) return null;
  if (e.action === "auth.sign_in_failed") return d.reason === "invalid_code" ? "Wrong code" : "Wrong password";
  if (e.action === "role.updated") {
    const parts = [];
    if (Array.isArray(d.added) && d.added.length) parts.push(`added ${d.added.join(", ")}`);
    if (Array.isArray(d.removed) && d.removed.length) parts.push(`removed ${d.removed.join(", ")}`);
    if (d.renamedFrom) parts.push(`renamed from ${String(d.renamedFrom)}`);
    return parts.join("; ") || null;
  }
  if (e.action === "settings.updated") {
    return Object.entries(d)
      .map(([k, v]) => `${k.split(".").pop()}: ${String((v as { to: unknown }).to)}`)
      .join("; ");
  }
  return null;
}

export function AuditPage() {
  const { me, can } = useSession();
  const [filters, setFilters] = useState({ actorId: "", action: "", from: "", to: "" });
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff"), enabled: can("staff.manage") });

  const query = useInfiniteQuery({
    queryKey: ["audit", filters],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: "50" });
      if (filters.actorId) qs.set("actorId", filters.actorId);
      if (filters.action) qs.set("action", filters.action);
      if (filters.from) qs.set("from", new Date(`${filters.from}T00:00:00`).toISOString());
      if (filters.to) qs.set("to", new Date(`${filters.to}T23:59:59`).toISOString());
      if (pageParam) qs.set("before", pageParam);
      return get<Page>(`/api/audit?${qs}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];
  const set = (k: keyof typeof filters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader title="Audit log" description="A permanent record of sign-ins and changes. Entries can't be edited or deleted." />

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {staff.data && (
          <SelectField label="Person" value={filters.actorId} onChange={set("actorId")}>
            <option value="">Everyone</option>
            {staff.data.map((s) => (
              <option key={s.id} value={s.id}>
                {s.firstName} {s.lastName}
              </option>
            ))}
          </SelectField>
        )}
        <SelectField label="Activity" value={filters.action} onChange={set("action")}>
          <option value="">All activity</option>
          {Object.entries(AUDIT_ACTIONS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </SelectField>
        <Field label="From" type="date" value={filters.from} onChange={set("from")} />
        <Field label="To" type="date" value={filters.to} onChange={set("to")} />
      </div>

      {query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <Alert>{errorMessage(query.error)}</Alert>
      ) : entries.length === 0 ? (
        <Panel className="px-5 py-10 text-center text-ink-soft">No activity matches these filters.</Panel>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base min-w-[720px]">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>What happened</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const extra = detailText(e);
                return (
                  <tr key={e.id}>
                    <td className="text-sm whitespace-nowrap text-ink-soft">{formatDateTime(e.occurredAt, me?.clinic.timezone)}</td>
                    <td className="font-medium">{e.actorName ?? "System"}</td>
                    <td>
                      <div className={e.action === "auth.sign_in_failed" ? "text-danger" : undefined}>{describe(e)}</div>
                      {extra && <div className="text-sm text-muted">{extra}</div>}
                    </td>
                    <td className="text-sm text-muted">{e.ip ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}

      {query.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button onClick={() => query.fetchNextPage()} busy={query.isFetchingNextPage}>
            Show older activity
          </Button>
        </div>
      )}
    </div>
  );
}
